// Placeholder diagnostic builders for invalid loop placeholder syntax.
// Example: `${{bp.size}` and `$bp.size` map to stable compiler errors here.
// Build the compile error message for legacy plain placeholder usage.
export function buildLegacyLoopPlaceholderError(source: string, index: number): string {
  const token = readLegacyLoopPlaceholder(source, index)
  const lineLabel = readLineLabel(source, index)
  // Return the stable syntax error message for plain placeholder usage.
  return `Invalid .luis syntax at line ${lineLabel}: plain loop placeholder "${token}" is not supported. Use braced form like "\${i}" or "\${bp.name}".`
}

// Build the compile error message for malformed braced placeholder usage.
export function buildMalformedLoopPlaceholderError(source: string, index: number): string {
  const token = readMalformedLoopPlaceholder(source, index)
  const lineLabel = readLineLabel(source, index)
  // Return the stable syntax error message for malformed braced placeholders.
  return `Invalid .luis syntax at line ${lineLabel}: malformed loop placeholder "${token}" is not supported. Use balanced braced form like "\${i}" or "\${bp.name}".`
}

// Build the compile error message for unresolved or invalid braced placeholders.
export function buildInvalidLoopPlaceholderError(token: string): string {
  // Return the stable syntax error message for invalid placeholder bodies.
  return `Invalid .luis syntax: loop placeholder "${token}" could not be resolved. Use valid forms like "\${i}" or "\${bp.name}".`
}

// Read the unsupported plain placeholder token for diagnostics.
function readLegacyLoopPlaceholder(source: string, start: number): string {
  let end = start + 1
  // Consume the plain placeholder token until a boundary char appears.
  while (end < source.length) {
    const char = source[end]
    if (
      (char >= 'a' && char <= 'z')
      || (char >= 'A' && char <= 'Z')
      || (char >= '0' && char <= '9')
      || char === '_'
      || char === '.'
      || char === '-'
    ) {
      end += 1
      continue
    }
    break
  }

  // Return the extracted legacy placeholder token.
  return source.slice(start, end)
}

// Read one malformed braced placeholder token for diagnostics.
function readMalformedLoopPlaceholder(source: string, start: number): string {
  let end = start + 1
  // Consume the malformed placeholder until a likely boundary char appears.
  while (end < source.length) {
    const char = source[end]
    if (char === '\n' || char === '\r' || char === ';') break
    end += 1
  }

  // Return the extracted malformed placeholder token.
  return source.slice(start, end).trim()
}

// Read the one-based source line for the provided token index.
function readLineLabel(source: string, index: number): number {
  let line = 1
  // Count line breaks before the failing token index.
  for (let cursor = 0; cursor < index; cursor += 1) {
    if (source[cursor] === '\n') {
      line += 1
    }
  }

  // Return the computed source line number.
  return line
}
